import { HttpService, Repository, RepositoryBase, Tags, Weaver } from '@skylab-shared/core';
import { AccountingOnlineReceiverModel, CompanyParametersDTO, CompanyParametersModel, HistoricalEntityModel, PersonKind } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { CompanyParametersBO } from '../company/company-parameters.bo';


@Repository({
    boType: CompanyParametersBO,
    apiController: {
        base: 'myCompany/properties',
        tags: [Tags.Api]
    },
    dto: {
        get: CompanyParametersDTO,
        update: CompanyParametersDTO
    }
})
export class CompanyParametersRepositoryBase extends RepositoryBase<CompanyParametersBO, CompanyParametersModel, CompanyParametersDTO, CompanyParametersDTO> {
    public constructor(
        private readonly weaver: Weaver,
        $http: HttpService,
    ) {
        super($http);
    }

    protected createBO(): CompanyParametersBO {
        return new CompanyParametersBO(this.weaver);
    }

    protected async preparePlainObjectAsync(model: CompanyParametersModel): Promise<CompanyParametersDTO> {
        return {
            version: model.version,

            advanceIncomeTaxPaymentFrequency: model.advanceIncomeTaxPaymentFrequency,
            cashBasis: model.cashBasis,
            formOfSettlement: model.formOfSettlement,
            logo: model.logo,
            otherSeller: model.otherSeller ? {
                address: model.otherSeller.address,
                city: model.otherSeller.city,
                countryId: model.otherSeller.countryId,
                issueDocumentsToOtherSeller: model.otherSeller.issueDocumentsToOtherSeller,
                roleRelativeToOtherSeller: model.otherSeller.roleRelativeToOtherSeller,
                name: model.otherSeller.name,
                nip: model.otherSeller.tin,
                zipCode: model.otherSeller.zipCode
            } : null,
            accountingOnlineReceiver: model.accountingOnlineReceiver ? {
                name: model.accountingOnlineReceiver.name,
                channelId: model.accountingOnlineReceiver.channelId,
                password: model.accountingOnlineReceiver.password,
                id: model.accountingOnlineReceiver.id
            } : null,
            personKind: model.personKind,
            taxOfficeCode: model.taxOfficeCode,
            ueVatPayer: model.ueVatPayer,
            vatPayer: model.vatPayer,
            vatReleaseReasonId: model.vatReleaseReasonId,
            vatReleaseReasonCustomName: model.vatReleaseReasonCustomName,
            vatReleaseReasonSource: model.vatReleaseReasonSource,
            vatReturnFrequency: model.vatReturnFrequency,
            zusContribution: model.zusContribution,
            ...(model.personKind === PersonKind.Natural
                ? {
                    taxpayerDateOfBirth: getStartOfDate(model.taxpayerDateOfBirth),
                    taxpayerFirstName: model.taxpayerFirstName,
                    taxpayerSurname: model.taxpayerSurname
                }
                : {}
            ),
            ossRegistered: model.ossRegistered,
            bdoNumber: model.bdoNumber,
            eoriNumber: model.eoriNumber,
            krsNumber: model.krsNumber,
            printBdoNumber: model.printBdoNumber,
            printEoriNumber: model.printEoriNumber,
            printKrsNumber: model.printKrsNumber,
            contactEmail: model.contactEmail,
            contactPhoneNumber: model.contactPhoneNumber
        };
    }

    protected async fromPlainObjectAsync(item: CompanyParametersDTO): Promise<CompanyParametersModel> {
        const converted = new CompanyParametersModel(pickBy<CompanyParametersModel>({
            version: item.version,

            advanceIncomeTaxPaymentFrequency: item.advanceIncomeTaxPaymentFrequency,
            cashBasis: item.cashBasis,
            formOfSettlement: item.formOfSettlement,
            logo: item.logo,
            otherSeller: item.otherSeller ? new HistoricalEntityModel(pickBy({
                address: item.otherSeller.address,
                city: item.otherSeller.city,
                countryId: item.otherSeller.countryId,
                roleRelativeToOtherSeller: item.otherSeller.roleRelativeToOtherSeller,
                issueDocumentsToOtherSeller: item.otherSeller.issueDocumentsToOtherSeller,
                name: item.otherSeller.name,
                tin: item.otherSeller.nip,
                zipCode: item.otherSeller.zipCode
            }, val => val !== void 0)) : null,
            accountingOnlineReceiver: item.accountingOnlineReceiver ? new AccountingOnlineReceiverModel(pickBy({
                name: item.accountingOnlineReceiver.name,
                channelId: item.accountingOnlineReceiver.channelId,
                password: item.accountingOnlineReceiver.password,
                id: item.accountingOnlineReceiver.id
            }, val => val !== void 0)) : null,
            personKind: item.personKind,
            taxOfficeCode: item.taxOfficeCode,
            taxpayerDateOfBirth: item.taxpayerDateOfBirth,
            taxpayerFirstName: item.taxpayerFirstName,
            taxpayerSurname: item.taxpayerSurname,
            ueVatPayer: item.ueVatPayer,
            vatPayer: item.vatPayer,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonSource: item.vatReleaseReasonSource,
            vatReturnFrequency: item.vatReturnFrequency,
            zusContribution: item.zusContribution,
            ossRegistered: item.ossRegistered,
            bdoNumber: item.bdoNumber,
            eoriNumber: item.eoriNumber,
            krsNumber: item.krsNumber,
            printBdoNumber: item.printBdoNumber,
            printEoriNumber: item.printEoriNumber,
            printKrsNumber: item.printKrsNumber,
            contactEmail: item.contactEmail,
            contactPhoneNumber: item.contactPhoneNumber
        }, val => val !== void 0));

        return converted;
    }
}
